/**
 * Minimal i18n store for SdI FatturaPA frontend.
 *
 * Currently only Italian ('it') is supported. Structure is kept generic so
 * additional locales can be added in the future by:
 *   1. Adding a new file under ./locales/<code>.json with the same keys.
 *   2. Registering it in LOCALES below.
 *   3. Exposing a language switcher that calls locale.set('<code>').
 */

import { derived, writable, get } from 'svelte/store';
import itLocale from './locales/it.json';

// Available locales — add more entries here when new translations are added.
export const LOCALES = {
  it: { name: 'Italiano', messages: itLocale },
};

// Active locale. Italian is the only supported language for now.
export const DEFAULT_LOCALE = 'it';
const LOCALE_KEY = 'sdi-locale';

function getInitialLocale() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored && LOCALES[stored]) return stored;
  } catch {
    // ignore storage errors
  }
  return DEFAULT_LOCALE;
}

export const locale = writable(getInitialLocale());

// Persist locale selection across sessions.
locale.subscribe((value) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LOCALE_KEY, value);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', value);
    }
  } catch {
    // ignore storage errors
  }
  // eslint-disable-next-line no-console
  console.debug('[i18n] locale set to', value);
});

/**
 * Resolve a dotted key against a nested messages object.
 * Returns undefined when the key is not found so callers can fall back.
 */
function resolveKey(messages, key) {
  if (!messages || !key) return undefined;
  const parts = key.split('.');
  let current = messages;
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Interpolate {placeholder} tokens in a translation string.
 * Example: interpolate('Hello {name}', { name: 'World' }) === 'Hello World'
 */
function interpolate(str, params) {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (_, p) => {
    const v = params[p];
    return v === undefined || v === null ? `{${p}}` : String(v);
  });
}

/**
 * Low-level translation lookup — usable outside of Svelte components.
 *   t('errors.generic')
 *   t('invoices.count', { n: 3 })
 *   t('missing.key', {}, 'fallback text')
 */
export function translate(localeCode, key, params, fallback) {
  const loc = LOCALES[localeCode] || LOCALES[DEFAULT_LOCALE];
  const msg = resolveKey(loc?.messages, key)
    ?? resolveKey(LOCALES[DEFAULT_LOCALE]?.messages, key);
  if (msg === undefined) {
    if (fallback !== undefined) return fallback;
    // eslint-disable-next-line no-console
    console.warn('[i18n] missing translation:', key);
    return key;
  }
  return interpolate(msg, params);
}

// Derived reactive translator for use in components: $t('key', params?)
export const t = derived(locale, ($locale) => (key, params, fallback) =>
  translate($locale, key, params, fallback),
);

// Non-reactive translator helper for use in plain JS (stores, utils).
export function tImmediate(key, params, fallback) {
  return translate(get(locale), key, params, fallback);
}
